import classNames from "classnames";
import { memo, type ReactNode } from "react";
import { Flex } from "../flex";
import { RedoIcon, type RedoIconColor } from "../icon/redo-icon";
import { Text } from "../text";
import type { TextColorValue } from "../theme/typography";
import * as css from "./redo-step-indicator.module.css";

export const redoStepIndicatorLead = ["icon", "number", "none"] as const;
export type RedoStepIndicatorLead = (typeof redoStepIndicatorLead)[number];

export const redoStepIndicatorAccent = ["primary", "brand", "success"] as const;
export type RedoStepIndicatorAccent = (typeof redoStepIndicatorAccent)[number];

// Layout axis, not the CRDB orientation enum. //redontIgnore
export const redoStepIndicatorDirection = ["horizontal", "vertical"] as const;
type RedoStepIndicatorDirection = (typeof redoStepIndicatorDirection)[number];

const redoStepIndicatorVariant = ["default", "timeline"] as const;
type RedoStepIndicatorVariant = (typeof redoStepIndicatorVariant)[number];

export const redoStepState = [
  "complete",
  "current",
  "partial",
  "upcoming",
] as const;
export type RedoStepState = (typeof redoStepState)[number];

export interface RedoStep {
  id: string;
  /** Step name. Hidden when `showName` is false. */
  label?: string;
  /** Custom lead icon (used when `lead="icon"`). An SVG icon component. */
  Icon?: React.FC<React.SVGProps<SVGSVGElement>>;
  /** Explicit state. When unset, state is derived from `activeIndex`. */
  state?: RedoStepState;
  disabled?: boolean;
  /** Secondary line under the name, shown only when `direction="vertical"`. */
  description?: ReactNode;
  /**
   * Secondary lines under the name, one element per entry; takes precedence
   * over `description`. Shown only when `direction="vertical"`.
   */
  descriptionLines?: string[];
}

export interface RedoStepIndicatorProps {
  "data-dd-action-name"?: string;
  steps: RedoStep[];
  /** Drives derived step state when a step has no explicit `state`. */
  activeIndex: number;
  /** Per-step lead indicator. `"none"` shows the name only. */
  lead?: RedoStepIndicatorLead;
  /** Show the step name. */
  showName?: boolean;
  /** Accent color for filled progress segments. */
  accent?: RedoStepIndicatorAccent;
  /** `"vertical"` stacks the steps as a list joined by a connector line. */
  direction?: RedoStepIndicatorDirection;
  /**
   * Vertical only. `"timeline"` widens the icon gap and step spacing, mutes
   * upcoming steps further, and shows the current step's description in
   * primary text.
   */
  variant?: RedoStepIndicatorVariant;
  /** Vertical only. Color of the current step's icon; defaults to `accent`. */
  currentAccent?: RedoStepIndicatorAccent;
  /** Append a "Step X of Y" caption. */
  showStepCount?: boolean;
  onStepClick?: (index: number, step: RedoStep) => void;
  /** Accessible label for the progressbar. */
  ariaLabel?: string;
  uiSelector?: string;
}

function deriveState(
  step: RedoStep,
  index: number,
  activeIndex: number,
): RedoStepState {
  if (step.state) {
    return step.state;
  }
  if (index < activeIndex) {
    return "complete";
  }
  if (index === activeIndex) {
    return "current";
  }
  return "upcoming";
}

const isFilled = (state: RedoStepState): boolean =>
  state === "complete" || state === "current" || state === "partial";

type AccentClasses = Record<RedoStepIndicatorAccent, string | undefined>;

const BAR_ACTIVE_CLASS: AccentClasses = {
  primary: css.hBarActivePrimary,
  brand: css.hBarActiveBrand,
  success: css.hBarActiveSuccess,
};

const LINE_ACTIVE_CLASS: AccentClasses = {
  primary: css.vLineActivePrimary,
  brand: css.vLineActiveBrand,
  success: css.vLineActiveSuccess,
};

const CURRENT_ICON_COLOR: Record<RedoStepIndicatorAccent, RedoIconColor> = {
  primary: "primary",
  brand: "brand",
  success: "success",
};

const StepLead = memo(function StepLead({
  step,
  index,
  state,
  lead,
  iconColor,
}: {
  step: RedoStep;
  index: number;
  state: RedoStepState;
  lead: RedoStepIndicatorLead;
  iconColor?: RedoIconColor;
}) {
  if (lead === "none") {
    return null;
  }
  if (lead === "icon") {
    // Consumer-chosen icon for the step. Accent never tints the icon —
    // only the progress bar.
    if (!step.Icon) {
      return null;
    }
    return (
      <RedoIcon
        color={iconColor ?? (state === "upcoming" ? "tertiary" : "primary")}
        Icon={step.Icon}
        size={16}
      />
    );
  }
  return (
    <Text
      fontSize="xs"
      fontWeight="regular"
      textColor={state === "upcoming" ? "secondary" : "primary"}
    >
      {index + 1}
    </Text>
  );
});

export const RedoStepIndicator = memo(function RedoStepIndicator({
  steps,
  activeIndex,
  lead = "number",
  showName = true,
  accent = "primary",
  showStepCount = false,
  direction = "horizontal",
  variant = "default",
  currentAccent,
  onStepClick,
  "data-dd-action-name": actionName,
  ariaLabel,
  uiSelector,
}: RedoStepIndicatorProps) {
  const derived = steps.map((step, index) =>
    deriveState(step, index, activeIndex),
  );
  const activeCount = derived.filter(isFilled).length;
  const barActiveClass = BAR_ACTIVE_CLASS[accent];

  if (direction === "vertical") {
    return (
      <VerticalStepIndicator
        accent={accent}
        activeIndex={activeIndex}
        ariaLabel={
          ariaLabel ?? `Progress: step ${activeCount} of ${steps.length}`
        }
        currentAccent={currentAccent ?? accent}
        data-dd-action-name={actionName}
        lead={lead}
        onStepClick={onStepClick}
        showName={showName}
        stepCount={showStepCount ? activeCount : undefined}
        steps={steps}
        uiSelector={uiSelector}
        variant={variant}
      />
    );
  }

  return (
    <Flex
      data-arbiter="RedoStepIndicator"
      data-dd-action-name={actionName}
      data-ui={uiSelector}
      dir="column"
      gap="sm"
      w="full"
    >
      <Flex
        align="flex-start"
        aria-label={
          ariaLabel ?? `Progress: step ${activeCount} of ${steps.length}`
        }
        aria-valuemax={steps.length}
        aria-valuemin={0}
        aria-valuenow={activeCount}
        dir="row"
        gap="sm"
        role="progressbar"
        w="full"
      >
        {steps.map((step, index) => {
          const state = deriveState(step, index, activeIndex);
          const active = isFilled(state);
          const ariaCurrent = state === "current" ? "step" : undefined;

          const header =
            lead !== "none" || (showName && step.label) ? (
              <Flex align="center" dir="row" gap="xs">
                <StepLead index={index} lead={lead} state={state} step={step} />
                {showName && step.label && (
                  <Text
                    fontSize="xs"
                    fontWeight="regular"
                    textColor={state === "upcoming" ? "secondary" : "primary"}
                  >
                    {step.label}
                  </Text>
                )}
              </Flex>
            ) : null;

          const bar = (
            <Flex
              className={classNames(css.hBar, active && barActiveClass)}
              w="full"
            />
          );

          return onStepClick ? (
            <Flex
              aria-current={ariaCurrent}
              as="button"
              className={classNames(
                css.clickable,
                step.disabled && css.disabled,
              )}
              cursor={step.disabled ? "not-allowed" : "pointer"}
              dir="column"
              disabled={step.disabled}
              gap="sm"
              grow={1}
              key={step.id}
              onClick={
                step.disabled ? undefined : () => onStepClick(index, step)
              }
            >
              {header}
              {bar}
            </Flex>
          ) : (
            <Flex
              aria-current={ariaCurrent}
              dir="column"
              gap="sm"
              grow={1}
              key={step.id}
            >
              {header}
              {bar}
            </Flex>
          );
        })}
      </Flex>
      {showStepCount && (
        <Flex alignSelf="flex-end">
          <Text fontSize="xs" textColor="secondary" whiteSpace="nowrap">
            {`Step ${activeCount} of ${steps.length}`}
          </Text>
        </Flex>
      )}
    </Flex>
  );
});

function verticalIconColor(
  state: RedoStepState,
  currentAccent: RedoStepIndicatorAccent,
  variant: RedoStepIndicatorVariant,
): RedoIconColor {
  switch (state) {
    case "current":
      return CURRENT_ICON_COLOR[currentAccent];
    case "partial":
      return "warning";
    case "upcoming":
      return variant === "timeline" ? "inherit" : "tertiary";
    case "complete":
      return "primary";
  }
}

function verticalLabelColor(
  state: RedoStepState,
  variant: RedoStepIndicatorVariant,
): TextColorValue {
  if (state !== "upcoming") {
    return "primary";
  }
  return variant === "timeline" ? "inherit" : "tertiary";
}

function verticalDescriptionColor(
  state: RedoStepState,
  variant: RedoStepIndicatorVariant,
): TextColorValue {
  if (variant === "timeline") {
    switch (state) {
      case "current":
        return "primary";
      case "upcoming":
        return "inherit";
      case "complete":
      case "partial":
        return "secondary";
    }
  }
  return state === "upcoming" ? "tertiary" : "secondary";
}

const VerticalStepIndicator = memo(function VerticalStepIndicator({
  steps,
  activeIndex,
  lead,
  showName,
  accent,
  currentAccent,
  variant,
  stepCount,
  onStepClick,
  "data-dd-action-name": actionName,
  ariaLabel,
  uiSelector,
}: {
  steps: RedoStep[];
  activeIndex: number;
  lead: RedoStepIndicatorLead;
  showName: boolean;
  accent: RedoStepIndicatorAccent;
  currentAccent: RedoStepIndicatorAccent;
  variant: RedoStepIndicatorVariant;
  /** Filled step count for the "Step X of Y" caption; omitted to hide it. */
  stepCount: number | undefined;
  onStepClick: RedoStepIndicatorProps["onStepClick"];
  "data-dd-action-name": string | undefined;
  ariaLabel: string;
  uiSelector: string | undefined;
}) {
  return (
    <Flex
      data-arbiter="RedoStepIndicator"
      data-dd-action-name={actionName}
      data-ui={uiSelector}
      dir="column"
      gap="sm"
      w="full"
    >
      <Flex
        aria-label={ariaLabel}
        as="ol"
        className={css.vList}
        dir="column"
        gap="none"
        m="none"
        p="none"
      >
        {steps.map((step, index) => {
          const state = deriveState(step, index, activeIndex);
          const last = index === steps.length - 1;
          const timeline = variant === "timeline";
          const subtle = timeline && state === "upcoming";
          const stepGap = timeline ? "xl" : "lg";
          const descriptionLines: ReactNode[] =
            step.descriptionLines ??
            (step.description ? [step.description] : []);
          const body = (
            <Flex dir="column" gap="xxs" pb={last ? undefined : stepGap}>
              {showName && step.label && (
                <Text
                  fontSize="sm"
                  fontWeight={state === "current" ? "semibold" : "regular"}
                  textColor={verticalLabelColor(state, variant)}
                >
                  {step.label}
                </Text>
              )}
              {descriptionLines.map((line, lineIndex) => (
                <Text
                  fontSize="xs"
                  key={lineIndex}
                  textColor={verticalDescriptionColor(state, variant)}
                >
                  {line}
                </Text>
              ))}
            </Flex>
          );

          return (
            <Flex
              aria-current={state === "current" ? "step" : undefined}
              as="li"
              className={classNames(subtle && css.vSubtle)}
              data-state={state}
              dir="row"
              gap={timeline ? "lg" : "md"}
              key={step.id}
            >
              <Flex
                align="center"
                className={css.vRail}
                dir="column"
                gap="xs"
                shrink={0}
              >
                <StepLead
                  iconColor={verticalIconColor(state, currentAccent, variant)}
                  index={index}
                  lead={lead}
                  state={state}
                  step={step}
                />
                {!last && (
                  <Flex
                    className={classNames(
                      css.vLine,
                      state === "complete" && LINE_ACTIVE_CLASS[accent],
                    )}
                    grow={1}
                  />
                )}
              </Flex>
              {onStepClick ? (
                <Flex
                  as="button"
                  className={classNames(
                    css.clickable,
                    step.disabled && css.disabled,
                  )}
                  cursor={step.disabled ? "not-allowed" : "pointer"}
                  disabled={step.disabled}
                  onClick={
                    step.disabled ? undefined : () => onStepClick(index, step)
                  }
                >
                  {body}
                </Flex>
              ) : (
                body
              )}
            </Flex>
          );
        })}
      </Flex>
      {stepCount !== undefined && (
        <Text fontSize="xs" textColor="secondary" whiteSpace="nowrap">
          {`Step ${stepCount} of ${steps.length}`}
        </Text>
      )}
    </Flex>
  );
});
